import { defaultTerminology } from "@/contexts/TerminologyContext";
import { useTerminology } from "@/hooks/useTerminology";
import { TerminologyOptions } from "@/types/v2";

export const useTerminologyReplacer = () => {
  const { terminology } = useTerminology();

  /**
   * Replaces all occurrences of a specific term in a string with a user-provided term, maintaining the capitalization style of the original term.
   *
   * This function considers four capitalization forms of the term:
   * - Lowercase (e.g., "example")
   * - Capitalized (e.g., "Example")
   * - Original capitalization (as provided in input)
   * - Uppercase (e.g., "EXAMPLE")
   *
   * For each form, it replaces the term in the string with the corresponding form of the user-provided term.
   * @param {string} str - The input string in which the term replacement will occur.
   * @param {string} defaultTerm - The term in the string to be replaced, which can exist in different capitalization forms.
   * @param {string} userTerm - The term to replace the `defaultTerm`, which will adopt capitalization forms corresponding to the original.
   * @returns {string} - The modified string with the term replacements applied. If any input is empty or the `defaultTerm` equals the `userTerm`, the original string is returned unmodified.
   */
  const replaceTermWithCapitalization = (
    str: string,
    defaultTerm: string,
    userTerm: string
  ): string => {
    if (!str || !defaultTerm || !userTerm || defaultTerm === userTerm) {
      return str;
    }

    // Escape special regex characters
    const escapeRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    // Use word boundaries for whole word matches
    const isMultiWord = defaultTerm.includes(" ");
    const wordBoundary = isMultiWord ? "" : "\\b"; // Only use word boundaries for single words

    // Create variations of the default term pattern
    const defaultLowerPattern = new RegExp(
      `${wordBoundary}${escapeRegExp(defaultTerm.toLowerCase())}${wordBoundary}`,
      "g"
    );

    const defaultFirstCap =
      defaultTerm.charAt(0).toUpperCase() + defaultTerm.slice(1).toLowerCase();
    const defaultFirstCapPattern = new RegExp(
      `${wordBoundary}${escapeRegExp(defaultFirstCap)}${wordBoundary}`,
      "g"
    );

    const defaultPattern = new RegExp(
      `${wordBoundary}${escapeRegExp(defaultTerm)}${wordBoundary}`,
      "g"
    );

    const defaultAllCapsPattern = new RegExp(
      `${wordBoundary}${escapeRegExp(defaultTerm.toUpperCase())}${wordBoundary}`,
      "g"
    );

    // Create corresponding variations of the user term
    const userLower = userTerm.toLowerCase();
    // Handle capitalization for multi-word user terms
    const isUserMultiWord = userTerm.includes(" ");
    let userFirstCap: string;

    if (isUserMultiWord) {
      // Capitalize each word in a multi-word term
      userFirstCap = userTerm
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    } else {
      userFirstCap =
        userTerm.charAt(0).toUpperCase() + userTerm.slice(1).toLowerCase();
    }

    const userAllCaps = userTerm.toUpperCase();

    // Replace all variations with corresponding capitalization
    let result = str;
    result = result.replace(defaultLowerPattern, userLower);
    result = result.replace(defaultFirstCapPattern, userFirstCap);
    result = result.replace(defaultPattern, userTerm);
    result = result.replace(defaultAllCapsPattern, userAllCaps);

    return result;
  };

  /**
   * Transforms a given string by replacing default terminology with user-defined terminology while preserving capitalization.
   *
   * This function processes a string and performs substitutions for specific terminology options
   * by replacing distinct forms (e.g., singular, plural, or acronym) of a default term with their
   * corresponding user-defined variants. The replacement occurs only if the default term exists
   * in the input string. Capitalization of the replaced terms in the input string is preserved.
   * @param {string} str - The input string to process. If the input is empty or null, it is returned as-is.
   * @param plurality str[] - The options for plurality
   * @returns {string} The transformed string with terminology substitutions applied.
   */
  const terminolizeAuto = (
    str: string,
    plurality: ("singular" | "plural" | "acronym")[] = [
      "singular",
      "plural",
      "acronym",
    ]
  ): string => {
    if (!str) return str;

    let result = str;

    // Process each terminology option
    Object.values(TerminologyOptions).forEach((option) => {
      // Process each form (singular, plural, acronym)
      plurality.forEach((form) => {
        const defaultTerm =
          defaultTerminology[option]?.[
            form as keyof (typeof defaultTerminology)[typeof option]
          ];
        const userTerm =
          terminology[option]?.[
            form as keyof (typeof terminology)[typeof option]
          ];

        if (defaultTerm && userTerm && defaultTerm !== userTerm) {
          // Check if the default term is present in any form in the string before attempting replacement
          const lowerStr = result.toLowerCase();
          const lowerTerm = defaultTerm.toLowerCase();

          if (lowerStr.includes(lowerTerm)) {
            result = replaceTermWithCapitalization(
              result,
              defaultTerm,
              userTerm
            );
          }
        }
      });
    });

    return result;
  };

  /**
   * Transforms a given string into its appropriate terminology format based on user-defined or default terminology options.
   * @param {string} str - The input string to be transformed.
   * @param {TerminologyOptions} option - The terminology option used to determine the transformation.
   * @param {"singular" | "plural" | "acronym"} [form] - Specifies the form of the terminology to apply (e.g., singular, plural, acronym).
   * @returns {string} - The transformed string with the applied terminology, maintaining original capitalization where appropriate.
   */
  const terminolize = (
    str: string,
    option: TerminologyOptions,
    form: "singular" | "plural" | "acronym" = "singular"
  ): string => {
    if (!str) return str;

    const defaultTerm = defaultTerminology[option]?.[form];
    const userTerm = terminology[option]?.[form];

    return replaceTermWithCapitalization(str, defaultTerm, userTerm);
  };

  return {
    terminolize,
    terminolizeAuto,
  };
};
